// =====================================================================================
// INVRT Radar — web console — one site — Ideal visitors tab
//
// The ICP (ideal customer profile) configuration for a site. The create form was moved
// verbatim out of SiteDetailPage.tsx in the C1 split; C1 (docs/API-CONTRACTS-C1.md §8,
// §10) adds the list, per-profile edit, delete and preview, and surfaces the advisories
// the server attaches to every write.
//
// SHAPES, read from routes/sites.ts:
//   GET    /sites/:id/icps              → {icps: [Icp]}  (sample_count is always null on a
//                                          list read; the provider is never called for it)
//   POST   /sites/:id/icps              → 201 Icp + {sample_count, advisories: string[]}
//   PATCH  /sites/:id/icps/:ordinal     → Icp + {sample_count, advisories}; `filters` is a
//                                          FULL REPLACE, never a merge, which is why the
//                                          edit dialog sends the whole set with the leaves it
//                                          does not expose carried over untouched
//   DELETE /sites/:id/icps/:ordinal     → {deleted: true, ordinal}
//   POST   /sites/:id/icps/preview      → {preview: <the provider's payload, verbatim>}
//
// Advisories are informational, never a refusal (computeIcpAdvisories in sites.ts), so
// they are shown as notices beside the saved profile, not as errors. The preview payload
// has no confirmed shape upstream; a count is recognised the same best-effort way the
// server recognises one (extractIcpSampleCount), and the full answer is always available.
//
// Create, edit and delete are organisation-admin actions on the server; preview is a read.
// Gated the same way here.
// =====================================================================================

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/hooks/useApi";
import { useSession } from "@/auth/SessionProvider";
import { ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { splitList } from "@/components/FacetField";
import { pluralise } from "@/lib/format";
import type { Site } from "@/lib/types";
import type {
  DeleteIcpResponse,
  Icp,
  IcpFilters,
  IcpList,
  PreviewIcpResponse,
  UpdateIcpRequest,
} from "@/lib/types.c1";

/**
 * What POST and PATCH actually return: the row, the live sample and the advisories. The
 * mirror in types.c1.ts closes the advisory vocabulary at three; the server also emits
 * `sample_size_unavailable` when its estimate call fails open, so the list is kept as
 * strings here and every value is mapped through ADVISORY_COPY below.
 */
type IcpWriteOnTheWire = Icp & { sample_count: number | null; advisories: string[] };

const ADVISORY_COPY: Record<string, string> = {
  icp_below_recommended_sample:
    "This profile matches fewer people than we recommend, so it may identify less of your traffic than you expect. It has been saved all the same.",
  icp_multiple_criteria_classes:
    "This profile combines several kinds of criteria, for example role and location. Each one narrows the match further; keep it if that is what you intend.",
  icp_open_profile:
    "This profile has no criteria, so every identified visitor matches it. That is a legitimate choice, and often the right one for a consumer site.",
  sample_size_unavailable:
    "The website identification service could not estimate how many people this profile matches just now. The profile was saved without an estimate.",
};

const ADVISORY_UNKNOWN =
  "The server added a note about this profile that this build does not recognise yet. The profile was saved.";

/** The same best-effort recognition the server applies (extractIcpSampleCount in sites.ts). */
const SAMPLE_COUNT_KEYS = [
  "totalCount",
  "total_count",
  "total",
  "sampleCount",
  "sample_count",
  "estimatedCount",
  "estimated_count",
  "estimated",
  "matchCount",
  "match_count",
  "matches",
  "count",
];

function extractSampleCount(raw: unknown, depth = 0): number | null {
  if (depth > 3) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return null;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
      const asNumber = Number(trimmed);
      return Number.isFinite(asNumber) ? asNumber : null;
    }
    try {
      return extractSampleCount(JSON.parse(trimmed), depth + 1);
    } catch {
      return null;
    }
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    for (const key of SAMPLE_COUNT_KEYS) {
      if (!(key in (raw as Record<string, unknown>))) continue;
      const nested = extractSampleCount((raw as Record<string, unknown>)[key], depth + 1);
      if (nested !== null) return nested;
    }
  }
  return null;
}

/** The provider's answer, verbatim and readable; an empty answer is said to be empty. */
function renderPayload(raw: unknown): string {
  if (raw === undefined || raw === null) return "The answer was empty.";
  if (typeof raw === "string") return raw;
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}

/** A compact, honest reading of a filter set for the list. */
function summariseFilters(filters: IcpFilters): string[] {
  const parts: string[] = [];
  if (filters.titles?.length) parts.push(`Titles: ${filters.titles.join(", ")}`);
  if (filters.levels?.length) parts.push(`Seniority: ${filters.levels.join(", ")}`);
  if (filters.functions?.length) parts.push(`Job functions: ${filters.functions.join(", ")}`);
  if (filters.industries?.linkedin?.length) {
    parts.push(`Industries: ${filters.industries.linkedin.join(", ")}`);
  }
  if (filters.industries?.sic?.length) parts.push(`SIC codes: ${filters.industries.sic.join(", ")}`);
  if (filters.location?.states?.length) {
    parts.push(`Regions: ${filters.location.states.join(", ")}`);
  }
  if (filters.location?.countries?.length) {
    parts.push(`Countries: ${filters.location.countries.join(", ")}`);
  }
  if (filters.location?.city_radius) {
    parts.push(
      `Near ${filters.location.city_radius.city} (${filters.location.city_radius.radius_km} km)`,
    );
  }
  if (filters.company) parts.push("Company criteria set");
  if (filters.signals) parts.push("Signal criteria set");
  if (filters.quality) parts.push("Contact-quality criteria set");
  if (filters.hygiene) parts.push("Hygiene rules set");
  if (filters.consumer) parts.push("Consumer criteria set");
  return parts;
}

interface EditedLeaves {
  titles: string[];
  levels: string[];
  industries: string[];
  states: string[];
}

/**
 * Writes the four leaves the dialog exposes back into the EXISTING filter set, leaving
 * every other leaf exactly as stored. PATCH replaces the whole set server-side, so sending
 * only the four would silently erase the rest of a profile.
 */
function applyEditedLeaves(existing: IcpFilters, edits: EditedLeaves): IcpFilters {
  const next: IcpFilters = { ...existing };

  if (edits.titles.length > 0) next.titles = edits.titles;
  else delete next.titles;

  if (edits.levels.length > 0) next.levels = edits.levels;
  else delete next.levels;

  const industries = { ...(existing.industries ?? {}) };
  if (edits.industries.length > 0) industries.linkedin = edits.industries;
  else delete industries.linkedin;
  if (Object.keys(industries).length > 0) next.industries = industries;
  else delete next.industries;

  const location = { ...(existing.location ?? {}) };
  if (edits.states.length > 0) location.states = edits.states;
  else delete location.states;
  if (Object.keys(location).length > 0) next.location = location;
  else delete next.location;

  return next;
}

interface EditState {
  ordinal: number;
  existing: IcpFilters;
  titles: string;
  levels: string;
  industries: string;
  states: string;
  active: boolean;
}

function editStateFor(icp: Icp): EditState {
  return {
    ordinal: icp.ordinal,
    existing: icp.filters,
    titles: (icp.filters.titles ?? []).join(", "),
    levels: (icp.filters.levels ?? []).join(", "),
    industries: (icp.filters.industries?.linkedin ?? []).join(", "),
    states: (icp.filters.location?.states ?? []).join(", "),
    active: icp.active,
  };
}

interface WriteOutcome {
  ordinal: number;
  sampleCount: number | null;
  advisories: string[];
}

export function IcpPanel({ site }: { site: Site }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { orgId, activeMembership } = useSession();
  const { push } = useToast();
  const [titles, setTitles] = useState("");
  const [levels, setLevels] = useState("");
  const [industries, setIndustries] = useState("");
  const [states, setStates] = useState("");
  const [editing, setEditing] = useState<EditState | null>(null);
  const [deleting, setDeleting] = useState<Icp | null>(null);
  const [outcome, setOutcome] = useState<WriteOutcome | null>(null);
  const [preview, setPreview] = useState<{ ordinal: number; raw: unknown } | null>(null);

  const listKey = ["icps", orgId, site.id];
  const icps = useQuery({
    queryKey: listKey,
    queryFn: () => api.get<IcpList>(`/sites/${site.id}/icps`),
    enabled: Boolean(orgId),
  });

  const create = useMutation({
    mutationFn: () =>
      api.post<IcpWriteOnTheWire>(`/sites/${site.id}/icps`, {
        filters: {
          ...(splitList(titles).length > 0 ? { titles: splitList(titles) } : {}),
          ...(splitList(levels).length > 0 ? { levels: splitList(levels) } : {}),
          ...(splitList(industries).length > 0
            ? { industries: { linkedin: splitList(industries) } }
            : {}),
          ...(splitList(states).length > 0 ? { location: { states: splitList(states) } } : {}),
        },
        active: true,
      }),
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      setTitles("");
      setLevels("");
      setIndustries("");
      setStates("");
      setOutcome({
        ordinal: saved.ordinal,
        sampleCount: saved.sample_count ?? null,
        advisories: Array.isArray(saved.advisories) ? saved.advisories : [],
      });
      push({ tone: "success", title: "Profile saved", body: "Radar will match against it from the next visit." });
    },
  });

  const update = useMutation({
    mutationFn: (state: EditState) => {
      const body: UpdateIcpRequest = {
        filters: applyEditedLeaves(state.existing, {
          titles: splitList(state.titles),
          levels: splitList(state.levels),
          industries: splitList(state.industries),
          states: splitList(state.states),
        }),
        active: state.active,
      };
      return api.patch<IcpWriteOnTheWire>(`/sites/${site.id}/icps/${state.ordinal}`, body);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      setEditing(null);
      setOutcome({
        ordinal: saved.ordinal,
        sampleCount: saved.sample_count ?? null,
        advisories: Array.isArray(saved.advisories) ? saved.advisories : [],
      });
      push({ tone: "success", title: "Profile updated", body: "Radar will match against the new criteria from the next visit." });
    },
  });

  const remove = useMutation({
    mutationFn: (icp: Icp) => api.delete<DeleteIcpResponse>(`/sites/${site.id}/icps/${icp.ordinal}`),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: listKey });
      setDeleting(null);
      setOutcome((current) => (current?.ordinal === result.ordinal ? null : current));
      setPreview((current) => (current?.ordinal === result.ordinal ? null : current));
      push({ tone: "info", title: "Profile deleted", body: "Radar no longer matches visitors against it." });
    },
  });

  const runPreview = useMutation({
    mutationFn: (icp: Icp) =>
      api.post<PreviewIcpResponse>(`/sites/${site.id}/icps/preview`, { filters: icp.filters }),
    onSuccess: (result, icp) => setPreview({ ordinal: icp.ordinal, raw: result.preview }),
  });

  const existing = icps.data?.icps ?? [];
  const isAdmin = activeMembership?.is_org_admin === true;
  const previewCount = preview ? extractSampleCount(preview.raw) : null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Who counts as a good visitor?</CardTitle>
          <p className="text-sm text-ink-muted">
            Radar matches identified visitors against these criteria so the people worth a
            call rise to the top. Leaving it empty means every identified visitor is shown.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {icps.isLoading ? <LoadingState label="Loading profiles…" /> : null}
          {icps.isError ? (
            <ErrorState error={icps.error} onRetry={() => void icps.refetch()} />
          ) : null}
          {icps.isSuccess ? (
            existing.length === 0 ? (
              <p className="text-sm text-ink-muted">No profile set for this site yet.</p>
            ) : (
              <>
                <p className="text-sm text-ink-muted">
                  {pluralise(existing.length, "profile")} configured for this site. A site can
                  hold up to 10.
                </p>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Profile</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Criteria</TableHeaderCell>
                      <TableHeaderCell className="text-right">Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {existing.map((icp) => {
                      const parts = summariseFilters(icp.filters);
                      return (
                        <TableRow key={icp.id}>
                          <TableCell className="whitespace-nowrap font-medium">
                            Profile {icp.ordinal}
                          </TableCell>
                          <TableCell>
                            <Badge tone={icp.active ? "success" : "neutral"}>
                              {icp.active ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-ink-muted">
                            {parts.length === 0 ? (
                              <span>Open profile: every identified visitor matches</span>
                            ) : (
                              parts.map((part) => (
                                <span key={part} className="block">
                                  {part}
                                </span>
                              ))
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex flex-wrap justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`Preview profile ${icp.ordinal}`}
                                loading={runPreview.isPending && runPreview.variables?.id === icp.id}
                                onClick={() => runPreview.mutate(icp)}
                              >
                                Preview
                              </Button>
                              {isAdmin ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Edit profile ${icp.ordinal}`}
                                    onClick={() => setEditing(editStateFor(icp))}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    aria-label={`Delete profile ${icp.ordinal}`}
                                    onClick={() => setDeleting(icp)}
                                  >
                                    Delete
                                  </Button>
                                </>
                              ) : null}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </>
            )
          ) : null}

          {runPreview.isError ? <ErrorState error={runPreview.error} /> : null}

          {preview ? (
            <div
              role="status"
              className="flex flex-col gap-2 rounded-card border border-primary-200 bg-primary-50 p-4 text-sm text-primary-700"
            >
              <p className="font-medium">Preview for profile {preview.ordinal}</p>
              {previewCount !== null ? (
                <p>
                  {previewCount.toLocaleString("en-GB")} matching people reported by the website
                  identification service.
                </p>
              ) : (
                <p>
                  The website identification service answered without a count this build
                  recognises. The full answer is below.
                </p>
              )}
              <details>
                <summary className="cursor-pointer text-xs">Full answer</summary>
                <pre className="mt-2 max-h-64 overflow-auto rounded-lg bg-white p-3 font-mono text-xs text-ink">
                  {renderPayload(preview.raw)}
                </pre>
              </details>
            </div>
          ) : null}

          {outcome ? (
            <div
              role="status"
              className="flex flex-col gap-2 rounded-card border border-primary-200 bg-primary-50 p-4 text-sm text-primary-700"
            >
              <p className="font-medium">Profile {outcome.ordinal} saved</p>
              {outcome.sampleCount !== null ? (
                <p>
                  The website identification service estimates{" "}
                  {outcome.sampleCount.toLocaleString("en-GB")} matching people.
                </p>
              ) : null}
              {outcome.advisories.map((advisory) => (
                <p key={advisory}>{ADVISORY_COPY[advisory] ?? ADVISORY_UNKNOWN}</p>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {isAdmin ? (
        <Card>
          <CardHeader>
            <CardTitle>Add a profile</CardTitle>
            <p className="text-sm text-ink-muted">
              Separate several values with commas. Every field is optional; a profile with no
              criteria matches every identified visitor.
            </p>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-4 md:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                create.mutate();
              }}
            >
              <Input
                label="Job titles"
                value={titles}
                onChange={(event) => setTitles(event.target.value)}
                placeholder="Finance Director, Head of Operations"
                hint="Separate several with commas."
              />
              <Input
                label="Seniority"
                value={levels}
                onChange={(event) => setLevels(event.target.value)}
                placeholder="C-Level, VP"
                hint="Separate several with commas."
              />
              <Input
                label="Industries"
                value={industries}
                onChange={(event) => setIndustries(event.target.value)}
                placeholder="Financial Services"
                hint="Separate several with commas."
              />
              <Input
                label="States or regions"
                value={states}
                onChange={(event) => setStates(event.target.value)}
                placeholder="Greater London"
                hint="Separate several with commas."
              />
              <div className="md:col-span-2">
                <Button type="submit" loading={create.isPending}>
                  Save this profile
                </Button>
              </div>
              {create.isError ? (
                <div className="md:col-span-2">
                  <ErrorState error={create.error} />
                </div>
              ) : null}
            </form>
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-ink-muted">
          An administrator on your team can change these criteria.
        </p>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={`Edit profile ${editing?.ordinal ?? ""}`}
        description="Radar matches against the new criteria from the next visit. Criteria this form does not show are kept as they are."
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              loading={update.isPending}
              onClick={() => editing && update.mutate(editing)}
            >
              Save changes
            </Button>
          </>
        }
      >
        {editing ? (
          <div className="flex flex-col gap-4">
            <Input
              label="Job titles"
              value={editing.titles}
              onChange={(event) => setEditing({ ...editing, titles: event.target.value })}
              hint="Separate several with commas."
            />
            <Input
              label="Seniority"
              value={editing.levels}
              onChange={(event) => setEditing({ ...editing, levels: event.target.value })}
              hint="Separate several with commas."
            />
            <Input
              label="Industries"
              value={editing.industries}
              onChange={(event) => setEditing({ ...editing, industries: event.target.value })}
              hint="Separate several with commas."
            />
            <Input
              label="States or regions"
              value={editing.states}
              onChange={(event) => setEditing({ ...editing, states: event.target.value })}
              hint="Separate several with commas."
            />
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(event) => setEditing({ ...editing, active: event.target.checked })}
              />
              Active
            </label>
            {update.isError ? <ErrorState error={update.error} /> : null}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={`Delete profile ${deleting?.ordinal ?? ""}?`}
        description="Radar stops matching visitors against it straight away. This cannot be undone."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              Delete it
            </Button>
          </>
        }
      >
        {remove.isError ? <ErrorState error={remove.error} /> : null}
      </Dialog>
    </div>
  );
}
